import { hexToRgb, mix, type BrandScale, type ColorScale } from 'pixelize-design-library';

/** `mix(a, b, weight)` takes `weight` of `b` — low rungs need a LOW weight of `hex500`. */
const LOW_RUNG_WEIGHTS: Record<50 | 100 | 200 | 300 | 400, number> = {
  50: 0.08,
  100: 0.2,
  200: 0.38,
  300: 0.58,
  400: 0.8
};
const HIGH_RUNG_WEIGHTS: Record<600 | 700 | 800 | 900, number> = {
  600: 0.8,
  700: 0.58,
  800: 0.38,
  900: 0.2
};

/** Pass (white, black) for a light-mode ramp, (black, white) for dark-mode's mirrored one. */
export function buildRamp(
  hex500: string,
  towardLowRungs: string,
  towardHighRungs: string
): ColorScale {
  const ramp = { 500: hex500 } as ColorScale;
  for (const [rung, weight] of Object.entries(LOW_RUNG_WEIGHTS)) {
    ramp[Number(rung) as keyof ColorScale] = mix(towardLowRungs, hex500, weight);
  }
  for (const [rung, weight] of Object.entries(HIGH_RUNG_WEIGHTS)) {
    ramp[Number(rung) as keyof ColorScale] = mix(towardHighRungs, hex500, weight);
  }
  return ramp;
}

const OPACITY_STEPS = [4, 8, 16, 24, 32, 40, 48] as const;

const byte = (n: number) => Math.round(n).toString(16).padStart(2, '0');

/** `#rrggbbaa`, not `rgba()` — library components read these back through `compositeOver`. */
export function buildBrandScale(hex500: string, towardLow: string, towardHigh: string): BrandScale {
  const { r, g, b } = hexToRgb(hex500);
  const rgb = `#${byte(r)}${byte(g)}${byte(b)}`;
  const opacity = Object.fromEntries(
    OPACITY_STEPS.map((step) => [step, `${rgb}${byte((step / 100) * 255)}`])
  ) as BrandScale['opacity'];
  return { ...buildRamp(hex500, towardLow, towardHigh), opacity };
}
